import apiClient from './client';
import { buildJsonApiQuery, normalizeErrors } from '@/utils/jsonapi';

/**
 * Unwrap the JSON:API-wrapped response the preview actions return.
 *
 * The preview and preview_unsaved actions are served by a ResourceViewSet, so
 * their response is JSON:API-wrapped ({ data: { attributes: { html,
 * used_sample_data } } }) even though the view returns a plain dict. Both
 * actions answer identically, so both callers share this.
 *
 * @param {Object} body - Raw axios `response.data`
 * @returns {{html: string, usedSampleData: boolean}}
 */
function unwrapPreviewResponse(body) {
  const attributes = body?.data?.attributes;
  return {
    html: attributes?.html ?? body?.html ?? '',
    usedSampleData: attributes?.used_sample_data ?? true,
  };
}

/**
 * Build the request body shared by preview, preview_unsaved and render_pdf:
 * report_id (or null for the sample context), an optional row limit, context
 * overrides, and any unsaved content overrides.
 *
 * @param {Object} options
 * @param {string} [options.reportId] - Report whose real data should render
 * @param {number} [options.limit] - Max generated rows when reportId is set
 * @param {Object} [options.context] - Variable overrides, applied last
 * @param {...*} [rest] - Content overrides (template_content, header_content,
 *   footer_content, css_styles) and, for render_pdf, filename
 * @returns {Object} Request body
 */
function buildRenderRequestBody({ reportId = null, limit, context = {}, ...rest }) {
  return {
    report_id: reportId || null,
    ...(limit !== undefined ? { limit } : {}),
    context,
    ...rest,
  };
}

/**
 * Recover the server's real error detail for a failed `responseType: 'blob'`
 * request.
 *
 * Axios applies `responseType: 'blob'` to error responses too, so a JSON
 * error body (e.g. `{errors: [{detail: 'Template syntax error: ...'}]}`)
 * arrives as an opaque Blob. The client interceptor's `normalizeErrors` runs
 * synchronously and can't read a Blob, so it falls through to a generic
 * "unexpected error" and `err.apiError.message` never carries the server's
 * actual detail. Read the Blob back out as JSON here and re-derive
 * `apiError` through the same normalization the interceptor uses, so
 * callers reading `err.apiError.message` see the real detail.
 *
 * @param {Object} err - Axios error, mutated in place with a corrected
 *   `apiError` when the body turns out to be parseable JSON
 * @returns {Promise<void>}
 */
async function deriveApiErrorFromBlobBody(err) {
  const response = err?.response;
  const blob = response?.data;
  if (!(blob instanceof Blob) || !/json/i.test(blob.type || '')) return;

  try {
    const data = JSON.parse(await blob.text());
    const normalizedErrors = normalizeErrors({ status: response.status, data });
    err.apiError = {
      status: response.status,
      code: normalizedErrors[0]?.code || data?.code || data?.error,
      message: normalizedErrors.length > 0 ? normalizedErrors[0]?.detail : err.message,
      errors: normalizedErrors,
      details: data?.details,
    };
  } catch {
    // Not parseable JSON after all - leave whatever apiError already exists.
  }
}

/**
 * Report Templates API - Handles all report template CRUD and rendering operations
 * Uses JSON:API format for standard CRUD, plain JSON for preview/render actions
 */
const reportTemplatesAPI = {
  /**
   * Get all report templates with optional filtering/pagination
   * @param {Object} options - JSON:API query options
   * @param {Object} requestOptions - Optional cancellation signal and pinned tenant headers
   * @returns {Promise<Object>} JSON:API response with data, meta, links
   */
  async getAll(options = {}, requestOptions = {}) {
    const params = buildJsonApiQuery(options);
    const response = await apiClient.get('/report-templates/', { ...requestOptions, params });
    return response.data;
  },

  /**
   * Get a single report template by ID
   * @param {string} id - Template UUID
   * @param {Object} options - JSON:API query options
   * @returns {Promise<Object>} JSON:API response with template data
   */
  async getById(id, options = {}, requestOptions = {}) {
    const params = buildJsonApiQuery(options);
    const response = await apiClient.get(`/report-templates/${id}/`, { ...requestOptions, params });
    return response.data;
  },

  /**
   * Create a new report template
   * @param {Object} templateData - Template data
   * @param {string} templateData.name - Template name
   * @param {string} [templateData.description] - Template description
   * @param {string} [templateData.template_content] - Body HTML content
   * @param {string} [templateData.header_content] - Header HTML content
   * @param {string} [templateData.footer_content] - Footer HTML content
   * @param {string} [templateData.css_styles] - CSS styles
   * @param {Object} [templateData.variables] - Template variables schema
   * @returns {Promise<Object>} JSON:API response with created template
   */
  async create(templateData) {
    const payload = {
      data: {
        type: 'report-template',
        attributes: templateData,
      },
    };
    const response = await apiClient.post('/report-templates/', payload);
    return response.data;
  },

  /**
   * Update an existing report template
   * @param {string} id - Template UUID
   * @param {Object} updates - Updated template data
   * @returns {Promise<Object>} JSON:API response with updated template
   */
  async update(id, updates) {
    const payload = {
      data: {
        type: 'report-template',
        id,
        attributes: updates,
      },
    };
    const response = await apiClient.patch(`/report-templates/${id}/`, payload);
    return response.data;
  },

  /**
   * Delete a report template
   * @param {string} id - Template UUID
   * @returns {Promise<void>}
   */
  async delete(id) {
    await apiClient.delete(`/report-templates/${id}/`);
  },

  /**
   * Get the tenant's default template library (system templates only).
   *
   * Backs the "Start from library" gallery: every row is read-only and is
   * meant to be cloned into an editable copy with `copy`, not edited here.
   *
   * @returns {Promise<Object>} JSON:API response with the library's templates
   */
  async getLibrary() {
    const response = await apiClient.get('/report-templates/library/');
    return response.data;
  },

  /**
   * Clone a template (typically a library/system one) into an editable copy
   * owned by the tenant. The copy is never a system template, regardless of
   * the source, and its name is suffixed to avoid colliding with the source.
   *
   * @param {string} id - Template UUID to copy
   * @returns {Promise<Object>} JSON:API response with the new template
   */
  async copy(id) {
    const response = await apiClient.post(`/report-templates/${id}/copy/`);
    return response.data;
  },

  /**
   * Preview a saved template by rendering it with either a real report's
   * generated data (`reportId`) or the server-side sample context.
   *
   * @param {string} id - Template UUID
   * @param {Object} [options]
   * @param {string} [options.reportId] - Report to render real data from;
   *   omitted or falsy renders the sample context instead
   * @param {number} [options.limit] - Max generated rows when reportId is set
   * @param {Object} [options.context] - Variable overrides, applied last
   * @param {string} [options.template_content] - Unsaved body to render
   * @param {string} [options.header_content] - Unsaved header to render
   * @param {string} [options.footer_content] - Unsaved footer to render
   * @param {string} [options.css_styles] - Unsaved CSS to render
   * @returns {Promise<{html: string, usedSampleData: boolean}>}
   */
  async preview(id, options = {}) {
    const response = await apiClient.post(
      `/report-templates/${id}/preview/`,
      buildRenderRequestBody(options)
    );
    return unwrapPreviewResponse(response.data);
  },

  /**
   * Preview a template that has not been saved yet, via the collection-level
   * preview endpoint. Content overrides are required here - there is no
   * saved template to fall back to - so Django tags still render for a
   * brand-new template.
   *
   * @param {Object} [options] - Same shape as `preview`'s options
   * @returns {Promise<{html: string, usedSampleData: boolean}>}
   */
  async previewUnsaved(options = {}) {
    const response = await apiClient.post(
      '/report-templates/preview/',
      buildRenderRequestBody(options)
    );
    return unwrapPreviewResponse(response.data);
  },

  /**
   * Render a saved template as a downloadable PDF.
   *
   * @param {string} id - Template UUID
   * @param {Object} [options] - Same shape as `preview`'s options, plus:
   * @param {string} [options.filename='report.pdf'] - Download filename
   * @returns {Promise<Blob>} PDF file blob
   */
  async renderPdf(id, options = {}) {
    const { filename = 'report.pdf', ...rest } = options;
    try {
      const response = await apiClient.post(
        `/report-templates/${id}/render_pdf/`,
        buildRenderRequestBody({ ...rest, filename }),
        { responseType: 'blob' }
      );
      return response.data;
    } catch (err) {
      await deriveApiErrorFromBlobBody(err);
      throw err;
    }
  },
};

export default reportTemplatesAPI;
